import { useEffect, useRef } from 'react';

/**
 * Manages keyboard navigation (ArrowUp, ArrowDown, Enter, Escape) for a
 * dropdown list. When `isOpen` becomes true, focuses the first list item
 * automatically.
 *
 * @param isOpen - Whether the dropdown is currently open.
 * @param listRef - Ref pointing to the `<ul>` (or container) of the dropdown.
 * @param onClose - Called when Escape is pressed.
 * @param itemSelector - CSS selector used to query focusable items inside `listRef`.
 * @param autoFocusOnOpen - Whether to auto-focus the first item when dropdown opens.
 * @param onArrowUpAtStart - Optional callback when ArrowUp is pressed at the first item.
 */
export function useDropdownKeyboardNav(
  isOpen: boolean,
  listRef: React.RefObject<HTMLElement | null>,
  onClose: () => void,
  itemSelector = 'button',
  autoFocusOnOpen = true,
  onArrowUpAtStart?: () => void,
) {
  const focusedIndexRef = useRef<number>(-1);

  const getItems = (): HTMLElement[] => {
    if (!listRef.current) return [];
    return Array.from(listRef.current.querySelectorAll<HTMLElement>(itemSelector));
  };

  const focusItem = (index: number) => {
    const items = getItems();
    if (items.length === 0) return;
    const clamped = Math.max(0, Math.min(index, items.length - 1));
    focusedIndexRef.current = clamped;
    items[clamped].focus();
  };

  // Auto-focus first item when dropdown opens
  useEffect(() => {
    if (isOpen) {
      focusedIndexRef.current = -1;
      if (autoFocusOnOpen) {
        // Defer to let the DOM render first
        requestAnimationFrame(() => focusItem(0));
      }
    } else {
      focusedIndexRef.current = -1;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Keydown handler to attach on the dropdown list element
  const handleKeyDown = (e: React.KeyboardEvent | KeyboardEvent) => {
    const items = getItems();
    if (items.length === 0) return;

    const current = focusedIndexRef.current;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusItem(current < 0 ? 0 : current + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (current <= 0 && onArrowUpAtStart) {
        // Call callback to return focus to input
        onArrowUpAtStart();
      } else {
        focusItem(current <= 0 ? 0 : current - 1);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'Enter' && current >= 0) {
      e.preventDefault();
      items[current].click();
    }
  };

  // Track focused index when user clicks or focuses an item directly
  const handleItemFocus = (index: number) => {
    focusedIndexRef.current = index;
  };

  return { handleKeyDown, handleItemFocus };
}
